/**
 * PriorityTag — Job / Task 优先级的一致视觉表达。
 *
 * 设计基调(精工克制):
 * - 4 档语义色板(低/中/高/紧急)对应 4 个饱和度渐进的 tone
 * - 左侧 2px vertical bar 强化色系,右侧文字,视觉分层
 * - 小圆角胶囊(4px)避免 antd Tag 的"票面感"
 * - 可编辑态 hover 时淡入 EditOutlined 图标,不打扰扫读
 *
 * 用途:JobList 表格列 / JobTaskList 页头 / UpdatePriorityModal 下拉 option。
 */
import type { CSSProperties, ReactNode } from 'react';
import { EditOutlined } from '@ant-design/icons';

interface PriorityTone {
  color: string;
  bg: string;
  bar: string;
}

// 值域 = PriorityEnum.id(2/6/9/10)。未在表内的值(理论不该出现)走灰色 fallback。
const TONE_BY_ID: Record<number, PriorityTone> = {
  2: { color: '#4b5563', bg: '#f3f4f6', bar: '#9ca3af' },   // 低:平静灰
  6: { color: '#1d4ed8', bg: '#eff6ff', bar: '#3b82f6' },   // 中:专业蓝(默认)
  9: { color: '#b45309', bg: '#fffbeb', bar: '#f59e0b' },   // 高:警示琥珀
  10: { color: '#b91c1c', bg: '#fef2f2', bar: '#ef4444' },  // 紧急:严重红
};

const FALLBACK_TONE: PriorityTone = {
  color: '#6b7280', bg: '#f9fafb', bar: '#d1d5db',
};

interface Props {
  /** PriorityEnum id;undefined/null 时组件返回 dash */
  priority?: number | null;
  /** 展示文案;缺省用"低/中/高/紧急"根据 priority 推(与后端 desc 一致) */
  label?: string;
  /** 可编辑态:hover 时淡入编辑图标,cursor:pointer;由父层控制点击 */
  editable?: boolean;
  onClick?: () => void;
  /** 尺寸;默认 'small'(表格/头部),弹窗内 options 用 'medium' */
  size?: 'small' | 'medium';
  style?: CSSProperties;
}

/** id → 中文名兜底(优先用外部传入的 label) */
const DEFAULT_LABEL: Record<number, string> = {
  2: '低',
  6: '中',
  9: '高',
  10: '紧急',
};

export default function PriorityTag({
  priority,
  label,
  editable = false,
  onClick,
  size = 'small',
  style,
}: Props) {
  if (priority == null) {
    return <span style={{ color: '#bfbfbf' }}>—</span>;
  }
  const tone = TONE_BY_ID[priority] ?? FALLBACK_TONE;
  const text = label ?? DEFAULT_LABEL[priority] ?? String(priority);
  const isMedium = size === 'medium';
  const paddingY = isMedium ? 3 : 1;
  const paddingRight = isMedium ? 10 : 8;
  const fontSize = isMedium ? 13 : 12;

  return (
    <>
      <span
        onClick={editable ? onClick : undefined}
        className={editable ? 'priority-tag priority-tag--editable' : 'priority-tag'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: `${paddingY}px ${paddingRight}px ${paddingY}px 0`,
          borderRadius: 4,
          background: tone.bg,
          color: tone.color,
          fontSize,
          fontWeight: 500,
          lineHeight: 1.4,
          fontVariantNumeric: 'tabular-nums',
          userSelect: 'none',
          cursor: editable ? 'pointer' : 'default',
          transition: 'background 0.14s ease',
          position: 'relative',
          overflow: 'hidden',
          ...style,
        }}
      >
        {/* 左侧 2px vertical bar */}
        <span
          aria-hidden
          style={{
            width: 3,
            alignSelf: 'stretch',
            background: tone.bar,
            marginRight: isMedium ? 8 : 6,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
          }}
        />
        <span>{text}</span>
        {editable && (
          <EditOutlined
            className="priority-tag__icon"
            style={{
              fontSize: isMedium ? 11 : 10,
              color: tone.bar,
              opacity: 0,
              transition: 'opacity 0.14s ease',
            }}
          />
        )}
      </span>
      {/* hover 状态样式:仅可编辑态背景加深、icon 淡入。用一次性 <style> 挂全局,
          零依赖(不引入 CSS module / less;不污染全局非本组件的选择器)。 */}
      <style>{`
        .priority-tag--editable:hover {
          filter: brightness(0.96);
        }
        .priority-tag--editable:hover .priority-tag__icon {
          opacity: 0.8 !important;
        }
      `}</style>
    </>
  );
}

/** 提供给 Select options render 用的辅助:label 位置塞 PriorityTag,值走 code。 */
export function priorityOptionRender(item: {
  code?: number;
  value?: string | number;
  label?: string;
  name?: string;
}): ReactNode {
  const id = Number(item.value ?? item.code);
  return (
    <PriorityTag
      priority={id}
      label={item.label ?? item.name ?? DEFAULT_LABEL[id] ?? String(id)}
      size="medium"
    />
  );
}
